<template>
  <div class="page-wrap">
    <div class="panel-card">

      <!-- Header -->
      <div class="panel-header">
        <div class="panel-header__icon"><i class="fa fa-user-tie"></i></div>
        <div class="panel-header__text">
          <div class="panel-header__title">Attorney</div>
          <div class="panel-header__sub">Manage attorney directory and documents</div>
        </div>
        <div class="panel-header__action">
          <button class="btn-print" @click="printReport">
            <i class="fa fa-print"></i> Print
          </button>
          <router-link :to="{ name: 'admin.attorney.create' }" class="btn-add">
            <i class="fa fa-plus"></i> Add
          </router-link>
        </div>
      </div>

      <!-- Toolbar -->
      <div class="toolbar">
        <div class="field-group field-group--search">
          <label>Search</label>
          <div class="search-wrap">
            <i class="fa fa-search search-icon"></i>
            <input v-model="searchInput" type="text" class="search-input" placeholder="Search by name or email…" />
          </div>
        </div>
        <div class="field-group">
          <label>Status</label>
          <div class="select-wrap">
            <select class="select-input" v-model="status">
              <option value="all">All Status</option>
              <option :value="true">Active</option>
              <option :value="false">Inactive</option>
            </select>
            <i class="fa fa-chevron-down select-arrow"></i>
          </div>
        </div>
        <div class="field-group">
          <label>Deleted</label>
          <div class="select-wrap">
            <select class="select-input" v-model="deletedFilter">
              <option value="active">Not Deleted</option>
              <option value="deleted">Deleted</option>
              <option value="all">All</option>
            </select>
            <i class="fa fa-chevron-down select-arrow"></i>
          </div>
        </div>
        <div class="toolbar__meta" v-if="!loadingData">
          {{ filteredList.length }} {{ filteredList.length === 1 ? 'entry' : 'entries' }}
        </div>
      </div>

      <!-- Loading -->
      <div class="state-box" v-if="loadingData">
        <div class="state-spinner"></div>
        <span>Loading attorneys…</span>
      </div>

      <!-- Empty -->
      <div class="state-box state-box--muted" v-else-if="filteredList.length === 0">
        <i class="fa fa-user-tie state-box__icon"></i>
        <div class="state-box__title">No attorneys found</div>
        <div class="state-box__sub">{{ searchInput ? 'Try a different search term' : 'Add the first attorney to get
          started' }}</div>
      </div>

      <!-- Table -->
      <div class="table-wrap" v-else>
        <table class="wl-table">
          <thead>
            <tr>
              <th class="th-sm">#</th>
              <th class="th-name">Attorney Name</th>
              <th class="th-contact">Contact Person</th>
              <th class="th-email">Email</th>
              <th class="th-phone">Contact No.</th>
              <th class="th-status">Status</th>
              <th class="th-approval">Approved</th>
              <th class="th-document">Document</th>
              <th class="th-action">Action</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(item, index) in filteredList" :key="item.id || index">
              <td class="td-sm">{{ index + 1 }}</td>
              <td class="td-name">{{ item.name }}</td>
              <td class="td-contact">{{ item.contact_person_name }}</td>
              <td class="td-email">
                <span class="email-badge"><i class="fa fa-envelope-o"></i> {{ item.email }}</span>
              </td>
              <td class="td-phone">{{ item.contact_no }}</td>
              <td class="td-status">
                <span v-if="item.is_deleted" class="badge-pill deleted">Deleted</span>
                <span v-else-if="item.is_active" class="badge-pill active">Active</span>
                <span v-else class="badge-pill inactive">Inactive</span>
              </td>
              <td class="td-approval">
                <span v-if="item.is_approved" class="badge-pill active">Approved</span>
                <span v-else class="badge-pill pending">Pending</span>
              </td>
              <td class="td-document">
                <span v-if="item.documents && item.documents.length" class="badge-pill active">Yes</span>
                <span v-else class="badge-pill inactive">No</span>
              </td>
              <td class="td-action">
                <button class="dots-btn" @click.stop="toggleMenu(index, $event)">
                  <i class="fa fa-ellipsis-v"></i>
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

    </div>

    <!-- 3-dot dropdown -->
    <div class="dot-menu" v-if="activeMenu !== null" :style="{ top: menuPos.top + 'px', right: menuPos.right + 'px' }"
      @click.stop>
      <button class="dot-item" @click="edit(menuItem); activeMenu = null">
        <i class="fa fa-pencil-alt"></i> Edit
      </button>
      <button class="dot-item" v-if="!menuItem.is_deleted" @click="openDeleteModal(menuItem); activeMenu = null">
        <i class="fa fa-trash"></i> Delete
      </button>
    </div>

    <!-- Delete modal -->
    <transition name="modal-fade">
      <div class="del-overlay" v-if="deleteModal.show" @click.self="deleteModal.show = false">
        <div class="del-box">
          <div class="del-icon"><i class="fa fa-trash"></i></div>
          <div class="del-title">Delete Attorney</div>
          <div class="del-sub">
            This will remove <strong>{{ deleteModal.name }}</strong> from the active attorney list.
          </div>
          <div class="del-actions">
            <button class="del-cancel" @click="deleteModal.show = false">Cancel</button>
            <button class="del-confirm" @click="confirmDelete">Delete</button>
          </div>
        </div>
      </div>
    </transition>

    <!-- Print-only report (hidden on screen, shown via @media print) -->
    <div class="print-report">
      <div class="print-report__header">
        <h1>Attorney List</h1>
        <div class="print-report__meta">Printed on {{ formatDate(new Date()) }} · {{ filteredList.length }} {{
          filteredList.length === 1 ? 'record' : 'records' }}</div>
      </div>
      <table class="print-report__table">
        <thead>
          <tr>
            <th>#</th>
            <th>Attorney Name</th>
            <th>Contact Person</th>
            <th>Email</th>
            <th>Contact No.</th>
            <th>Status</th>
            <th>Approval</th>
            <th>Added Date</th>
            <th>Approved Date</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(item, index) in filteredList" :key="item.id || index">
            <td>{{ index + 1 }}</td>
            <td>{{ item.name }}</td>
            <td>{{ item.contact_person_name }}</td>
            <td>{{ item.email }}</td>
            <td>{{ item.contact_no }}</td>
            <td>
              <template v-if="item.is_deleted">Deleted</template>
              <template v-else-if="item.is_active">Active</template>
              <template v-else>Inactive</template>
            </td>
            <td>{{ item.is_approved ? 'Approved' : 'Pending' }}</td>
            <td>{{ formatDate(item.created_at) }}</td>
            <td>{{ item.approved_at ? formatDate(item.approved_at) : '—' }}</td>
          </tr>
        </tbody>
      </table>
    </div>

  </div>
</template>

<script>
import moment from "moment";
import {
  GET_ATTORNEY_LIST,
  DELETE_ATTORNEY
} from "@/core/services/store/actions.type";
import { SET_BREADCRUMB } from "@/core/services/store/mutations.type";

export default {
  computed: {
    filteredList() {
      const q = this.searchInput.toLowerCase();
      return this.attorneys.filter(i => {
        if (this.status !== "all" && !!i.is_active !== this.status) return false;
        if (this.deletedFilter === "active" && i.is_deleted) return false;
        if (this.deletedFilter === "deleted" && !i.is_deleted) return false;
        if (!q) return true;
        return (
          (i.name || "").toLowerCase().includes(q) ||
          (i.email || "").toLowerCase().includes(q) ||
          (i.contact_person_name || "").toLowerCase().includes(q)
        );
      });
    }
  },
  data() {
    return {
      loadingData: false,
      attorneys: [],
      searchInput: "",
      status: true,
      deletedFilter: "active",
      activeMenu: null,
      menuPos: { top: 0, right: 0 },
      menuItem: null,
      deleteModal: { show: false, id: null, name: "" },
      breadCrumbs: [
        { id: 1, title: "Dashboard", route: "admin.dashboard" },
        { id: 2, title: "Attorney", route: "" }
      ]
    };
  },
  mounted() {
    this.$store.commit(SET_BREADCRUMB, this.breadCrumbs);
    this.fetchList();
    document.addEventListener("click", this.closeMenu);
  },
  beforeDestroy() {
    document.removeEventListener("click", this.closeMenu);
  },
  methods: {
    fetchList() {
      this.loadingData = true;
      this.$store.dispatch(GET_ATTORNEY_LIST).then(res => {
        this.attorneys = res.data;
        this.loadingData = false;
      }).catch(() => { this.loadingData = false; });
    },
    formatDate(date) {
      return date ? moment(date).format("MM/DD/YYYY") : "—";
    },
    printReport() {
      var kt_subheader = document.getElementById("kt_subheader");
      var kt_header = document.getElementById("kt_header");
      var kt_copyright_footer = document.getElementById("kt_copyright_footer");
      var kt_aside_parent = document.getElementById("kt_aside_parent");

      if (kt_header) { kt_header.classList.add("kt_header_toggle"); kt_header.style.visibility = "hidden"; }
      if (kt_subheader) kt_subheader.style.visibility = "hidden";
      if (kt_copyright_footer) kt_copyright_footer.style.visibility = "hidden";
      if (kt_aside_parent) kt_aside_parent.style.visibility = "hidden";
      document.body.classList.add("aside-minimize");

      window.print();

      if (kt_copyright_footer) kt_copyright_footer.style.visibility = "visible";
      if (kt_subheader) kt_subheader.style.visibility = "visible";
      if (kt_header) { kt_header.style.visibility = "visible"; kt_header.classList.remove("kt_header_toggle"); }
      if (kt_aside_parent) kt_aside_parent.style.visibility = "visible";
      document.body.classList.remove("aside-minimize");
    },
    edit(item) {
      this.$router.push({ name: "admin.attorney.edit", params: { id: item.id } });
    },
    toggleMenu(index, event) {
      if (this.activeMenu === index) { this.activeMenu = null; return; }
      const r = event.currentTarget.getBoundingClientRect();
      this.menuPos = { top: r.bottom + 4, right: window.innerWidth - r.right };
      this.menuItem = { ...this.filteredList[index] };
      this.activeMenu = index;
    },
    closeMenu() { this.activeMenu = null; },
    openDeleteModal(item) {
      this.deleteModal = { show: true, id: item.id, name: item.name };
    },
    confirmDelete() {
      const id = this.deleteModal.id;
      this.deleteModal.show = false;
      this.$store.dispatch(DELETE_ATTORNEY, id).then(() => {
        this.$toastr.s("Attorney Successfully Deleted");
        this.fetchList();
      });
    }
  }
};
</script>

<style lang="scss" scoped>
$font: 'Segoe UI', sans-serif;
$blue: #389ad4;
$dark: #0d1b3e;
$border: #dde2ec;

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.page-wrap {
  padding: 12px;
  font-family: $font;
}

.panel-card {
  background: #fff;
  border: 1px solid $border;
  border-radius: 16px;
  overflow: clip;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.05);
}

.panel-header {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 20px 24px;
  border-bottom: 1px solid $border;
  background: linear-gradient(135deg, #f8faff 0%, #f0f5ff 100%);

  &__icon {
    width: 48px;
    height: 48px;
    border-radius: 12px;
    background: linear-gradient(135deg, $blue, #2277aa);
    display: flex;
    align-items: center;
    justify-content: center;
    color: #fff;
    font-size: 18px;
    flex-shrink: 0;
  }

  &__text {
    flex: 1;
  }

  &__title {
    font-size: 16px;
    font-weight: 700;
    color: $dark;
    line-height: 1.2;
  }

  &__sub {
    font-size: 12px;
    color: #7a8499;
    margin-top: 3px;
  }

  &__action {
    display: flex;
    align-items: center;
    gap: 10px;
  }
}

.btn-print {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 34px;
  padding: 0 16px;
  border-radius: 8px;
  background: #fff;
  color: $blue;
  border: 1.5px solid $blue;
  font-size: 13px;
  font-weight: 600;
  font-family: $font;
  white-space: nowrap;
  cursor: pointer;
  transition: background 0.15s;

  &:hover {
    background: rgba(56, 154, 212, 0.08);
  }

  i {
    font-size: 11px;
  }
}

.btn-add {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 34px;
  padding: 0 16px;
  border-radius: 8px;
  background: $blue;
  color: #fff;
  font-size: 13px;
  font-weight: 600;
  font-family: $font;
  text-decoration: none;
  border: none;
  white-space: nowrap;
  transition: background 0.15s, box-shadow 0.15s;

  &:hover {
    background: #2d85bb;
    box-shadow: 0 4px 12px rgba(56, 154, 212, 0.35);
    color: #fff;
    text-decoration: none;
  }

  i {
    font-size: 11px;
  }
}

.toolbar {
  display: flex;
  align-items: flex-end;
  gap: 12px;
  padding: 16px 20px;
  border-bottom: 1px solid $border;
  background: #fafbfd;
  flex-wrap: wrap;

  &__meta {
    margin-left: auto;
    font-size: 12px;
    color: #7a8499;
    white-space: nowrap;
    align-self: center;
  }
}

.field-group {
  display: flex;
  flex-direction: column;
  gap: 5px;
  min-width: 180px;

  &--search {
    min-width: 260px;
  }

  label {
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: #7a8499;
  }
}

.search-wrap {
  position: relative;
  width: 100%;
}

.search-icon {
  position: absolute;
  left: 10px;
  top: 50%;
  transform: translateY(-50%);
  color: #a0aab8;
  font-size: 12px;
  pointer-events: none;
}

.search-input,
.select-input {
  height: 38px;
  border: 1.5px solid $border;
  border-radius: 8px;
  font-size: 13px;
  color: $dark;
  font-family: $font;
  outline: none;
  background: #fff;
  transition: border-color 0.2s, box-shadow 0.2s;

  &:hover {
    border-color: $blue;
  }

  &:focus {
    border-color: $blue;
    box-shadow: 0 0 0 3px rgba(56, 154, 212, 0.1);
  }
}

.select-wrap {
  position: relative;
}

.select-input {
  padding: 0 30px 0 10px;
  cursor: pointer;
  width: 100%;
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
}

.select-arrow {
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 10px;
  color: #a0aab8;
  pointer-events: none;
}

.search-input {
  width: 100%;
  padding: 0 10px 0 30px;

  &::placeholder {
    color: #b0bac9;
  }
}

.state-box {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 56px 24px;
  gap: 8px;
  color: #7a8499;
  font-size: 13px;

  &--muted .state-box__icon {
    color: #c5d0e0;
  }

  &__icon {
    font-size: 36px;
    margin-bottom: 4px;
  }

  &__title {
    font-size: 15px;
    font-weight: 600;
    color: $dark;
  }

  &__sub {
    font-size: 13px;
  }
}

.state-spinner {
  width: 32px;
  height: 32px;
  border: 3px solid #c5d8f5;
  border-top-color: $blue;
  border-radius: 50%;
  animation: spin 0.7s linear infinite;
  margin-bottom: 8px;
}

.table-wrap {
  overflow-x: auto;
  overflow-y: auto;
  max-height: calc(100vh - 220px);
}

.wl-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
  color: $dark;

  th {
    position: sticky;
    top: 0;
    z-index: 1;
    padding: 10px 16px;
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: #7a8499;
    border-bottom: 2px solid $border;
    white-space: nowrap;
    background: #fafbfd;
  }

  .th-sm {
    width: 52px;
    text-align: center;
  }

  .th-name {
    width: 17%;
  }

  .th-contact {
    width: 14%;
  }

  .th-email {
    width: 20%;
  }

  .th-phone {
    width: 12%;
  }

  .th-status {
    width: 9%;
  }

  .th-approval {
    width: 9%;
    text-align: center;
  }

  .th-document {
    width: 9%;
    text-align: center;
  }

  .th-action {
    width: 60px;
    text-align: center;
  }

  td {
    padding: 11px 16px;
    border-bottom: 1px solid #f0f2f8;
    vertical-align: middle;
  }

  tr:hover td {
    background: #f8fbff;
  }

  tr:last-child td {
    border-bottom: none;
  }

  .td-sm {
    text-align: center;
    color: #a0aab8;
    font-size: 12px;
  }

  .td-name {
    font-weight: 500;
  }

  .td-contact {
    color: #5a6278;
    font-size: 12.5px;
  }

  .td-approval {
    text-align: center;
  }

  .td-document {
    text-align: center;
  }

  .td-action {
    text-align: center;
  }
}

.email-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 12.5px;
  color: $blue;
  background: rgba(56, 154, 212, 0.08);
  padding: 3px 10px;
  border-radius: 20px;
  font-weight: 500;

  i {
    font-size: 11px;
  }
}

.badge-pill {
  display: inline-flex;
  align-items: center;
  padding: 3px 10px;
  border-radius: 20px;
  font-size: 11px;
  font-weight: 700;

  &.active {
    background: #c6f6d5;
    color: #276749;
  }

  &.inactive {
    background: #e2e8f0;
    color: #4a5568;
  }

  &.deleted {
    background: #fed7d7;
    color: #c53030;
  }

  &.pending {
    background: #fefcbf;
    color: #7b6e1a;
  }
}

.dots-btn {
  width: 30px;
  height: 30px;
  border: none;
  border-radius: 7px;
  background: transparent;
  color: #a0aab8;
  font-size: 14px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: background 0.15s, color 0.15s;

  &:hover {
    background: #f0f3fa;
    color: $dark;
  }
}

.dot-menu {
  position: fixed;
  z-index: 9999;
  background: #fff;
  border: 1.5px solid $border;
  border-radius: 10px;
  box-shadow: 0 8px 24px rgba(13, 27, 62, 0.12);
  padding: 4px;
  min-width: 130px;
}

.dot-item {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 8px 12px;
  border: none;
  background: transparent;
  font-size: 13px;
  font-family: $font;
  color: $dark;
  cursor: pointer;
  border-radius: 7px;
  text-align: left;
  transition: background 0.12s;

  i {
    font-size: 12px;
    color: #7a8499;
  }

  &:hover {
    background: #f4f6fb;
  }
}

.del-overlay {
  position: fixed;
  inset: 0;
  z-index: 1000;
  background: rgba(13, 27, 62, 0.45);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
}

.del-box {
  background: #fff;
  border-radius: 16px;
  padding: 32px 28px 24px;
  width: 380px;
  text-align: center;
  box-shadow: 0 20px 60px rgba(13, 27, 62, 0.2);
}

.del-icon {
  width: 52px;
  height: 52px;
  border-radius: 50%;
  background: rgba(180, 35, 24, 0.1);
  color: #b42318;
  font-size: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 14px;
}

.del-title {
  font-size: 16px;
  font-weight: 700;
  color: $dark;
  margin-bottom: 8px;
}

.del-sub {
  font-size: 13px;
  color: #7a8499;
  line-height: 1.5;
  margin-bottom: 24px;

  strong {
    color: $dark;
  }
}

.del-actions {
  display: flex;
  gap: 10px;
}

.del-cancel {
  flex: 1;
  height: 38px;
  border-radius: 8px;
  border: 1.5px solid $border;
  background: #fff;
  color: $dark;
  font-size: 13px;
  font-weight: 600;
  font-family: $font;
  cursor: pointer;

  &:hover {
    background: #f4f6fb;
  }
}

.del-confirm {
  flex: 1;
  height: 38px;
  border-radius: 8px;
  border: none;
  background: #b42318;
  color: #fff;
  font-size: 13px;
  font-weight: 600;
  font-family: $font;
  cursor: pointer;

  &:hover {
    background: #8f1a12;
  }
}

.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity 0.2s;
}

.modal-fade-enter,
.modal-fade-leave-to {
  opacity: 0;
}

/* ── Print report (only visible when printing) ── */
.print-report {
  display: none;
}

@media print {

  .page-wrap>.panel-card,
  .dot-menu,
  .del-overlay {
    display: none !important;
  }

  .print-report {
    display: block;
  }

  .print-report__header {
    margin-bottom: 16px;
    border-bottom: 2px solid $dark;
    padding-bottom: 10px;

    h1 {
      font-size: 20px;
      font-weight: 700;
      color: $dark;
      margin: 0 0 4px;
    }
  }

  .print-report__meta {
    font-size: 12px;
    color: #7a8499;
  }

  .print-report__table {
    width: 100%;
    border-collapse: collapse;
    font-size: 11px;

    th,
    td {
      padding: 6px 8px;
      text-align: left;
      border-bottom: 1px solid $border;
    }

    th {
      font-weight: 700;
      text-transform: uppercase;
      font-size: 10px;
      color: #5a6278;
    }

    td {
      color: $dark;
    }
  }
}
</style>
